import React, { useContext, useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../helpers/AuthContext';

function Navbar() {
  const { authState, handleLogout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [retraiteOpen, setRetraiteOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const optionsRef = useRef(null);
  const retraiteRef = useRef(null);
  const settingsRef = useRef(null);

  // Fermer les dropdowns si clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (optionsRef.current && !optionsRef.current.contains(e.target)) setOptionsOpen(false);
      if (retraiteRef.current && !retraiteRef.current.contains(e.target)) setRetraiteOpen(false);
      if (settingsRef.current && !settingsRef.current.contains(e.target)) setSettingsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const logout = (e) => {
    e.preventDefault();
    handleLogout();
    navigate('/');
  };

  const isAdmin = authState.user?.roles === 'SUPERADMIN' || authState.user?.roles === 'ADMIN';
  const isSuperAdmin = authState.user?.roles === 'SUPERADMIN';

  const col1 = [
    { href: '/createpersonnel', icon: 'fa-address-card', label: 'Nouveau Personnel' },
    { href: '/personnel', icon: 'fa-archive', label: 'Liste Personnel' },
    { href: '/statisticsbyorganique', icon: 'fa-shield', label: 'C. Organique' },
    { href: '/statisticsbyoperationnel', icon: 'fa-adjust', label: 'Effectifs Opérationnels' },
  ];

  const col2 = [
    { href: '/listeboasss', icon: 'fa-shield', label: 'Liste Boa et DSS' },
    { href: '/decoration', icon: 'fa-id-badge', label: 'Décoration' },
    { href: '/compagnie', icon: 'fa-building', label: 'Compagnie' },
    { href: '/bataillon', icon: 'fa-sitemap', label: 'Bataillon' },
  ];

  const col3Admin = [
    { href: '/listusers', icon: 'fa-users', label: 'USERS' },
    { href: '/etsouservice', icon: 'fa-building', label: 'Ets ou Services' },
    { href: '/grade', icon: 'fa-star', label: 'Grade' },
    { href: '/listministresuccessifs', icon: 'fa-university', label: 'Ministres successifs' },
    { href: '/etatmajoruccessifs', icon: 'fa-shield', label: "Chef d'Etat-Major" },
  ];

  const col4Admin = [
    { href: '/directeurs', icon: 'fa-user', label: 'Directeur de la DCCA' },
    { href: '/zonededefenses', icon: 'fa-map-marker', label: 'Zone de défense' },
    { href: '/importpersonnel', icon: 'fa-upload', label: 'Importation personnel' },
    { href: '/doublonspersonnels', icon: 'fa-clone', label: 'Liste des doublons' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-900 border-b border-gray-700 shadow-lg">
      <div className="max-w-full px-4 flex items-center justify-between h-14">

        {/* Logo / Home */}
        <a href="/" className="flex items-center gap-2 text-white hover:text-green-400 transition-colors duration-200">
          <i className="fa fa-home fa-lg"></i>
          <span className="text-sm font-semibold tracking-widest uppercase text-green-400">DCCA</span>
        </a>

        {/* Burger mobile */}
        <button
          className="md:hidden text-white hover:text-green-400 transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`fa ${mobileOpen ? 'fa-times' : 'fa-bars'} fa-lg`}></i>
        </button>

        {/* Menu desktop */}
        <div className="hidden md:flex items-center gap-1 flex-1 ml-6">

          {/* OPTIONS dropdown */}
          <div className="relative" ref={optionsRef}>
            <button
              onClick={() => { setOptionsOpen(!optionsOpen); setRetraiteOpen(false); setSettingsOpen(false); }}
              className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${optionsOpen ? 'bg-green-600 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}
            >
              OPTIONS
              <i className={`fa fa-chevron-down text-xs transition-transform duration-200 ${optionsOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {optionsOpen && (
              <div className="absolute top-full left-0 mt-2 bg-gray-800 border border-gray-600 rounded-xl shadow-2xl p-4 z-50"
                style={{ width: isAdmin ? '800px' : '500px' }}>
                <div className="grid gap-6" style={{ gridTemplateColumns: isAdmin ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)' }}>

                  {/* Colonne 1 */}
                  <div>
                    <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-2 px-2">Personnel</p>
                    {col1.map((item) => (
                      <a key={item.href} href={item.href}
                        className="flex items-center gap-2 px-2 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors duration-150">
                        <i className={`fa ${item.icon} text-green-400 w-4 text-center`}></i>
                        {item.label}
                      </a>
                    ))}
                  </div>

                  {/* Colonne 2 */}
                  <div>
                    <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-2 px-2">Organisation</p>
                    {col2.map((item) => (
                      <a key={item.href} href={item.href}
                        className="flex items-center gap-2 px-2 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors duration-150">
                        <i className={`fa ${item.icon} text-green-400 w-4 text-center`}></i>
                        {item.label}
                      </a>
                    ))}
                  </div>

                  {/* Colonne 3 Admin */}
                  {isAdmin && (
                    <div>
                      <p className="text-xs font-semibold text-yellow-400 uppercase tracking-wider mb-2 px-2">
                        <i className="fa fa-lock mr-1"></i> Admin
                      </p>
                      {col3Admin.map((item) => (
                        <a key={item.href} href={item.href}
                          className="flex items-center gap-2 px-2 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors duration-150">
                          <i className={`fa ${item.icon} text-yellow-400 w-4 text-center`}></i>
                          {item.label}
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Colonne 4 Admin */}
                  {isAdmin && (
                    <div>
                      <p className="text-xs font-semibold text-yellow-400 uppercase tracking-wider mb-2 px-2">
                        <i className="fa fa-cog mr-1"></i> Gestion
                      </p>
                      {col4Admin.map((item) => (
                        <a key={item.href} href={item.href}
                          className="flex items-center gap-2 px-2 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors duration-150">
                          <i className={`fa ${item.icon} text-yellow-400 w-4 text-center`}></i>
                          {item.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Statistiques (SUPERADMIN) */}
          {isSuperAdmin && (
            <a href="/statistics"
              className="px-3 py-2 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-colors duration-200">
              Statistiques
            </a>
          )}

          {/* Retraité dropdown */}
          <div className="relative" ref={retraiteRef}>
            <button
              onClick={() => { setRetraiteOpen(!retraiteOpen); setOptionsOpen(false); setSettingsOpen(false); }}
              className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${retraiteOpen ? 'bg-green-600 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}
            >
              Retraité
              <i className={`fa fa-chevron-down text-xs transition-transform duration-200 ${retraiteOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {retraiteOpen && (
              <div className="absolute top-full left-0 mt-2 w-72 bg-gray-800 border border-gray-600 rounded-xl shadow-2xl p-2 z-50">
                <a href="/personnelsmilitaireretraite"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors duration-150">
                  <i className="fa fa-address-card text-green-400 w-4 text-center"></i>
                  Personnel Militaire dans 2 ans
                </a>
                <a href="/personnelscivilretraite"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors duration-150">
                  <i className="fa fa-archive text-green-400 w-4 text-center"></i>
                  Personnel Civil dans 2 ans
                </a>
              </div>
            )}
          </div>

          {/* Honorifique */}
          <a href="/decoration"
            className="px-3 py-2 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-colors duration-200">
            Honorifique
          </a>

          {/* Carte (SUPERADMIN) */}
          {isSuperAdmin && (
            <a href="/cartebrigademadagascar"
              className="px-3 py-2 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-colors duration-200">
              Carte
            </a>
          )}
        </div>

        {/* Settings dropdown */}
        <div className="relative hidden md:block" ref={settingsRef}>
          <button
            onClick={() => { setSettingsOpen(!settingsOpen); setOptionsOpen(false); setRetraiteOpen(false); }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-300 hover:bg-gray-700 hover:text-white transition-colors duration-200"
          >
            <i className="fa fa-cog fa-lg"></i>
            {authState.isAuthenticated && (
              <span className="text-xs text-green-400 font-medium">{authState.user?.username}</span>
            )}
          </button>

          {settingsOpen && (
            <div className="absolute top-full right-0 mt-2 w-52 bg-gray-800 border border-gray-600 rounded-xl shadow-2xl p-2 z-50">
              {authState.isAuthenticated ? (
                <>
                  <div className="px-3 py-2 border-b border-gray-600 mb-1">
                    <p className="text-xs text-gray-400">Connecté en tant que</p>
                    <p className="text-sm font-semibold text-white">{authState.user?.username}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-green-700 text-green-200 rounded-full">
                      {authState.user?.roles}
                    </span>
                  </div>
                  <button
                    onClick={logout}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-900/30 hover:text-red-300 rounded-lg transition-colors duration-150"
                  >
                    <i className="fa fa-sign-out w-4 text-center"></i>
                    Déconnexion
                  </button>
                </>
              ) : (
                <Link to="/login"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors duration-150">
                  <i className="fa fa-sign-in w-4 text-center"></i>
                  Se connecter
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Menu mobile */}
      {mobileOpen && (
        <div className="md:hidden bg-gray-800 border-t border-gray-700 px-4 py-3 space-y-1">
          {[...col1, ...col2].map((item) => (
            <a key={item.href} href={item.href}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg">
              <i className={`fa ${item.icon} text-green-400 w-4 text-center`}></i>
              {item.label}
            </a>
          ))}
          {isAdmin && [...col3Admin, ...col4Admin].map((item) => (
            <a key={item.href} href={item.href}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg">
              <i className={`fa ${item.icon} text-yellow-400 w-4 text-center`}></i>
              {item.label}
            </a>
          ))}
          <a href="/decoration" className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg">
            <i className="fa fa-star text-green-400 w-4 text-center"></i>
            Honorifique
          </a>
          {authState.isAuthenticated ? (
            <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-900/30 rounded-lg">
              <i className="fa fa-sign-out w-4 text-center"></i>
              Déconnexion
            </button>
          ) : (
            <Link to="/login" className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 rounded-lg">
              <i className="fa fa-sign-in w-4 text-center"></i>
              Se connecter
            </Link>
          )}
        </div>
      )}
    </nav>
  );
}

export default Navbar;